<template>
  <div class="configurator-zone-list">
    <ul class="configurator-zone-list__group">
      <li
        v-for="group in items"
        :key="group.id"
        class="configurator-zone-list__group-item"
      >
        <div
          v-if="hasMultipleGroups"
          class="configurator-zone-list__group-item-header"
        >
          <div
            class="configurator-zone-list__group-item-name"
            v-text="group.name"
          ></div>
        </div>

        <div class="configurator-zone-list__group-item-body">
          <ul
            v-if="group.customizations.length"
            class="configurator-zone-list__zone-list"
          >
            <li
              v-for="customization in group.customizations"
              :key="customization.id"
              :ref="getCustomizationRef(customization.id)"
              class="configurator-zone-list__zone-item"
            >
              <InputContentFactory
                :name="customization.id"
                :type="ZoneTypeMap[customization.type]"
                :value="
                  getInputValue(
                    customization,
                    group.zoneMap[customization.templateZoneId]
                  )
                "
                :custom-product="activeProduct && activeProduct.isCustomizable"
                v-bind="{
                  group,
                  customization,
                }"
                @input="
                  setZoneCustomizationValue(customization, group.id, $event)
                "
                @focus="onFocus(customization.id)"
                @blur="onBlur"
              />
            </li>
          </ul>

          <div v-if="activeProduct && activeProduct.isCustomizable">
            <ConfiguratorAddCustomizationDropdownMenu
              v-bind="{ ...getAllowedCustomizationsForGroup(group.zoneMap) }"
              @action-click="addCustomization($event, group.id, group.zoneMap)"
            />
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import InputContentFactory from './InputContentFactory';
import { ZoneType, ZoneTypeMap } from '../../api/static/zones';
import {
  ConfiguratorGetters,
  ConfiguratorMutation,
  mapGetters as mapConfiguratorGetters,
  prefixType as prefixConfiguratorType,
} from '../../store/modules/configurator';
import debounce from 'lodash/debounce';
import ConfiguratorAddCustomizationDropdownMenu from './ConfiguratorAddCustomizationDropdownMenu';

export default {
  name: 'ConfiguratorZoneList',
  components: { ConfiguratorAddCustomizationDropdownMenu, InputContentFactory },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      ZoneTypeMap,
    };
  },
  computed: {
    ...mapConfiguratorGetters({
      activeProduct: ConfiguratorGetters.GET_ACTIVE_PRODUCT,
    }),
    hasMultipleGroups() {
      return this.items.length > 1;
    },
  },
  methods: {
    /**
     * Returns a unique ref name for a customization item.
     * @param {string} customizationId
     * @returns {string}
     */
    getCustomizationRef(customizationId) {
      return `customization-${customizationId}`;
    },
    /**
     * Focuses the first customization input field.
     * This method is exposed for external components to call via $refs.
     * @returns {boolean} - Returns true if focus was successful, false otherwise.
     */
    focusFirstField() {
      if (!this.items.length) {
        return false;
      }

      const firstGroup = this.items[0];
      if (!firstGroup.customizations.length) {
        return false;
      }

      const firstCustomization = firstGroup.customizations[0];
      const refName = this.getCustomizationRef(firstCustomization.id);
      const refElement = this.$refs[refName];

      if (refElement && refElement[0]) {
        const input = refElement[0].querySelector('input, textarea');
        if (input) {
          input.focus();
          return true;
        }
      }

      return false;
    },
    getInputValue(customization, zone) {
      if (customization.type === ZoneType.TEXT) {
        return customization.text === undefined
          ? zone.defaultValue
          : customization.text;
      } else if (customization.type === ZoneType.IMAGE) {
        return customization.url === undefined
          ? zone.defaultValue
          : customization.url;
      } else {
        throw new Error('Unknown zone type');
      }
    },
    setZoneCustomizationValue: debounce(function (
      customization,
      groupId,
      text
    ) {
      this.updateZoneCustomizationValue(customization, groupId, text);

      globalThis.dispatchEvent(
        new CustomEvent('lgf.configurator.customizationUpdate', {
          detail: {
            content_type: customization.type,
          },
        })
      );
    },
    500),
    updateZoneCustomizationValue(customization, groupId, text) {
      this.$store.commit(
        prefixConfiguratorType(ConfiguratorMutation.SET_ZONE_CUSTOMIZATION),
        {
          groupId,
          customization: { ...customization, text },
        }
      );
    },
    onFocus(customizationId) {
      this.$store.commit(
        prefixConfiguratorType(
          ConfiguratorMutation.SET_FOCUSED_CUSTOMIZATION_ID
        ),
        customizationId
      );
    },
    onBlur() {
      this.$store.commit(
        prefixConfiguratorType(
          ConfiguratorMutation.SET_FOCUSED_CUSTOMIZATION_ID
        ),
        null
      );
    },
    addCustomization(type, groupId, templateZoneMap) {
      const templateZone = Object.values(templateZoneMap).find(
        templateZone => templateZone.type === type
      );

      if (!templateZone) {
        throw new Error('Template zone not found.');
      }

      const templateZoneId = templateZone.id;

      this.$store.commit(
        prefixConfiguratorType(ConfiguratorMutation.SET_ZONE_CUSTOMIZATION),
        {
          groupId,
          customization: { type, templateZoneId },
        }
      );
    },
    getAllowedCustomizationsForGroup(zoneMap) {
      const templateZonePerType = Object.values(zoneMap).reduce(
        (acc, templateZone) => {
          acc[templateZone.type] = templateZone;

          return acc;
        },
        {}
      );

      return {
        allowTextCustomizations:
          templateZonePerType[ZoneType.TEXT] !== undefined,
        allowImageCustomizations:
          templateZonePerType[ZoneType.IMAGE] !== undefined,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-zone-list {
  &__group {
    margin-bottom: 1em;
  }

  &__group-item {
    margin-bottom: 2em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__group-item-header {
    margin-bottom: 0.5rem;
  }

  &__group-item-name {
    font-weight: 600;
    color: $color-arapawa;
    position: relative;
    display: inline-block;
    text-transform: uppercase;
  }

  &__group-item-body {
    margin-bottom: 0.5em;
  }

  &__zone-list {
    padding-left: 0;
    list-style: none;
  }

  &__zone-item {
    margin-bottom: 0.5em;
  }
}
</style>
